// AuditHistoryDrawer — CTSK-389 (Phase 4 of EPIC CTSK-385).
//
// Reusable slide-in panel showing one resource's full resource_audit
// timeline (GET /audit?resourceType=<x>&resourceId=<y>). Meant to be
// wired into resource detail pages as a "History" affordance.
//
// Only DocumentDetail.tsx and StorageContainerDetail.tsx are actual
// per-resource detail pages in this repo today (verified by grep —
// tags / tools / nests / custom properties are managed through
// list+inline-edit admin panels with no per-row detail route to drop a
// tab or drawer trigger into). This component is deliberately
// resource-type-agnostic so it's ready to wire into a tag/tool/nest/CP
// detail page if/when one ships.

import { useQuery } from '@tanstack/react-query';
import type { AuditEventRow, AuditEventType, AuditResourceType } from '@captain-tasko/shared';
import { apiList, ApiError } from '../lib/api.js';
import { SkeletonRows } from './Skeleton.js';

const EVENT_TYPE_LABELS: Record<AuditEventType, string> = {
  created: 'Created',
  updated: 'Updated',
  soft_deleted: 'Soft-deleted',
  undeleted: 'Undeleted',
  hard_deleted: 'Hard-deleted',
  restored: 'Restored',
  renamed: 'Renamed',
};

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** Same resolved-name-with-visible-fallback contract as AuditPanel's
 *  ActorCell: resolved display name when the API could batch-resolve
 *  `actorUserId`, otherwise the raw ULID rendered in muted monospace
 *  (never silently hidden). */
function actorLabel(row: AuditEventRow): { text: string; muted: boolean } | null {
  if (row.actor) return { text: row.actor.name ?? row.actor.id, muted: false };
  if (row.actorUserId) return { text: row.actorUserId, muted: true };
  return null;
}

export interface AuditHistoryDrawerProps {
  resourceType: AuditResourceType;
  resourceId: string;
  onClose: () => void;
}

/** Slide-in drawer (fixed-position overlay, right-anchored) — same
 *  overlay-click-to-close convention as AuditPanel's drill-down modal. */
export function AuditHistoryDrawer({ resourceType, resourceId, onClose }: AuditHistoryDrawerProps) {
  const q = useQuery({
    queryKey: ['audit-history', resourceType, resourceId],
    queryFn: () =>
      apiList<AuditEventRow>('/audit', {
        query: { resourceType, resourceId, limit: 100 },
      }),
    enabled: !!resourceId,
  });

  const rows = q.data?.data ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resource history"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420, maxWidth: '92vw', height: '100%', overflowY: 'auto',
          background: 'var(--surface-raised)', borderLeft: '1px solid var(--border-strong)',
          padding: 20, boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>History</h3>
          <button type="button" onClick={onClose} aria-label="Close history" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>

        {q.isLoading ? (
          <SkeletonRows rows={5} />
        ) : q.isError ? (
          (() => {
            const status = q.error instanceof ApiError ? q.error.status : 0;
            return (
              <div style={{ fontSize: 13, color: status === 403 ? 'var(--text-muted)' : 'var(--danger-primary, #B3261E)' }}>
                {status === 403
                  ? <>Viewing history requires the <code>audit:view</code> capability.</>
                  : 'Failed to load history.'}
              </div>
            );
          })()
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No audit events recorded for this resource.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((r) => (
              <li key={r.id} style={{ borderLeft: '2px solid var(--border-strong)', paddingLeft: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{EVENT_TYPE_LABELS[r.eventType]}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatTimestamp(r.eventAt)}</div>
                {(() => {
                  const label = actorLabel(r);
                  if (!label) return null;
                  return (
                    <div
                      style={{
                        fontSize: 11,
                        color: label.muted ? 'var(--text-muted)' : 'var(--text-secondary)',
                        fontFamily: label.muted ? 'var(--font-mono)' : undefined,
                      }}
                    >
                      by {label.text}
                    </div>
                  );
                })()}
                {r.reason && <div style={{ fontSize: 12, marginTop: 2 }}>{r.reason}</div>}
                {r.changesJson && 'before' in r.changesJson && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ fontSize: 11, cursor: 'pointer', color: 'var(--accent-primary)' }}>Diff</summary>
                    <pre style={{ fontSize: 10, background: 'var(--surface-sunken)', padding: 6, borderRadius: 4, overflowX: 'auto' }}>
                      {JSON.stringify(r.changesJson, null, 2)}
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
